/**
 * Helpers function to work eChart's dataset.
 */

import {
    ChartSettingsT,
    OrderedColumnsT,
    TimeScales,
} from 'business/definitions';
import { METRIC_KEY } from 'helpers/dataHelpers/pivot';
import { isYearlyComparisonScale } from 'helpers/yearlyComparison/scale';
import { getYearlyComparisonSettingsForScale } from 'helpers/yearlyComparison/settings';
import { some, map, filter, find, isObjectLike } from 'lodash';
import { columnOrder } from '../Chart/graphs/TableChart/defaultSettings';
import { AxisDimensionT, Dataset, SeriePointT } from '../Chart/types';
import { getSortedDataset, getSortedXValues } from './sortHelpers';

/**
 * Default dataset id.
 */
export const DEFAULT_DATASET_NAME = 'base';

/**
 * Data type supported by echart dataset.
 */
type EChartDatasetType = 'time' | 'ordinal' | 'number';

/**
 * A dataset's dimension.
 */
export type DatasetDimension = {
    name: string;
    type: EChartDatasetType;
};

/**
 * Type predicate to check if a ResponseDataValueT is a DictAllDataValueT.
 * @param {ResponseDataValueT} point - The data point to test.
 */
export const isTree = (point: ResponseDataValueT): point is DictAllDataValueT =>
    isObjectLike(point);

/**
 * eChart dataset use a limited number of types.
 * For this reason we need to convert some of our more specific data type
 * to more generic one supported by datasets.
 * It has no impact since dataset can automaticaly detect for date/time/datetime.
 *
 * @param mType - The Manty DataTypesT to convert.
 */
const convertMantyTypeToDatasetType = (
    mType: DataTypesT | undefined,
): EChartDatasetType => {
    switch (mType) {
        case 'date':
            return 'time';
        case 'time':
            return 'time';
        case 'datetime':
            return 'time';
        case 'category':
            return 'ordinal';
        case 'text':
            return 'ordinal';
        case 'float':
            return 'number';
        case 'integer':
            return 'number';
        default:
            console.warn('Unhandled Manty type:', mType);
            return 'number';
    }
};

/**
 * Sort an array of `ResponseDataValueT` to make it compatible with yearly comparison.
 * Effectively, this mean it will sort by month or day of the week, so that the first (january, monday)
 * is on the left, and the last (december, sunday) is on the right.
 *
 * @param xKey - the key for the X axis.
 * @param data - the rows of data to sort.
 */
const sortForYearlyComparison = (xKey: string, data: ResponseDataT[]) => {
    const scaleIsValid = isYearlyComparisonScale(xKey);
    const yearlyComparisonSettings = scaleIsValid
        ? getYearlyComparisonSettingsForScale(xKey)
        : undefined;
    // Sorting according to the month (or day or quarter) in case of a yearly comparison.
    if (yearlyComparisonSettings) {
        const sortDateNames = yearlyComparisonSettings.sort;
        data.sort((a, b) =>
            sortDateNames(a[xKey]!.toString(), b[xKey]!.toString()),
        );
    }
};

/**
 * When a data point contains tree-type data, this function will
 * add a flat version by adding a dimension with the label and one
 * with the value.
 *
 * @param {string} dimension - The dimension to flatten.
 * @param {ResponseDataT} point - The data point to check and flatten.
 * @returns {ResponseDataT} The same point but with a flatten version of any tree data contained in the point.
 */
const flattenPoint = (
    dimension: string,
    point: ResponseDataT,
): ResponseDataT => {
    const data = point[dimension];
    if (isTree(data)) {
        const valueKey = `${dimension}.value`;
        const labelKey = `${dimension}.label`;
        const flat: Record<string, PlainResponseDataValueT> = {
            [valueKey]: data.value,
            [labelKey]: data.label,
        };
        return {
            ...point,
            ...flat,
        };
    }
    return point;
};

/**
 * Check if a type is used to represent time (date, time, year, ...).
 *
 * @param {string | undefined} type - The type string to test.
 * @returns {boolean} Return true if the type represent time.
 */
const isTimeType = (type: string | undefined): boolean => {
    switch (type) {
        case 'time':
        case 'date':
        case 'year':
        case 'datetime':
            return true;
        default:
            return false;
    }
};

const DOW_ORDER = [
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
    'SUNDAY',
];

/**
 * Sort the data by day of the week.
 * This will sort the data in-place.
 * @param data - Data to sort (mutated).
 * @param key - The key to use for sorting. Priority is given to `rawXData` if it exists.
 * @param order - The order of the days of the week.
 * @returns The sorted data.
 */
export function sortByDayOfWeek(
    data: ResponseDataT[],
    key: string,
): ResponseDataT[] {
    return data.sort((a, b) => {
        const aValue = a.rawXData !== undefined ? a.rawXData : a[key];
        const bValue = b.rawXData !== undefined ? b.rawXData : b[key];
        const aIndex = DOW_ORDER.indexOf(
            aValue != null ? aValue.toString().toUpperCase() : '',
        );
        const bIndex = DOW_ORDER.indexOf(
            bValue != null ? bValue.toString().toUpperCase() : '',
        );
        return aIndex - bIndex;
    });
}

/**
 * Sort the data by:
 * - Time if there is an axis with a time type (prioritize the X axis).
 * - Value if the Y axis has a number type.
 * This modify the data in-place (not immutable).
 * @param {ResponseDataT[]} data - Data to sort (in-place / mutated).
 * @param {OrderedColumnsT} orderedColumns - Columns definitions.
 */
const standardSort = (
    data: ResponseDataT[],
    orderedColumns: OrderedColumnsT,
    xAxisValues: ResponseDataValueT[] | null = null,
) => {
    const xColumn = orderedColumns.x;
    const timeDimension =
        xColumn && isTimeType(xColumn.type)
            ? xColumn
            : find(orderedColumns, (col) => isTimeType(col?.type));
    const yColumn = orderedColumns.y;
    if (xColumn?.key === METRIC_KEY) {
        data.sort((a, b) => {
            const aIndex = columnOrder.indexOf(a[METRIC_KEY] as string);
            const bIndex = columnOrder.indexOf(b[METRIC_KEY] as string);
            return aIndex - bIndex;
        });
    } else if (timeDimension && isTimeType(timeDimension.type)) {
        data.sort((a, b) => {
            const aValue = a[timeDimension.key as keyof typeof a];
            const bValue = b[timeDimension.key as keyof typeof b];

            const isInvalid = (val: ResponseDataValueT): boolean =>
                val === 'unknown';
            if (isInvalid(aValue) && !isInvalid(bValue)) return 1;
            if (!isInvalid(aValue) && isInvalid(bValue)) return -1;

            return (aValue as number) - (bValue as number);
        });
    } else if (xAxisValues && xColumn) {
        data.sort((a, b) => {
            const aIndex = xAxisValues.indexOf(a[xColumn.key]);
            const bIndex = xAxisValues.indexOf(b[xColumn.key]);
            return aIndex - bIndex;
        });
    } else if (xColumn && xColumn.scale === TimeScales.dow) {
        sortByDayOfWeek(data, xColumn.key);
    } else if (convertMantyTypeToDatasetType(yColumn.type) === 'number') {
        // @ts-expect-error same reason.
        data.sort((a, b) => b[yColumn.key] - a[yColumn.key]);
    }
};

/** Get the source for a serie.
 * @param {ResponseDataValueT} serieKey - The serie key to filter on.
 * @param {ResponseDataT[]} data - The source data.
 * @param {string} zAxisKey - The Z axis key.
 * @returns {ResponseDataT[]} The data source.
 */
const getSource = (
    serieKey: ResponseDataValueT,
    data: ResponseDataT[],
    zAxisKey: string,
): ResponseDataT[] =>
    filter(data, (d) => {
        const point = d[zAxisKey];
        if (isTree(point)) {
            return point.value === serieKey;
        }
        return point === serieKey;
    });

/** Get the source for a serie, and fill the missing data with null.
 * @param {ResponseDataValueT} serieKey - The serie key to filter on.
 * @param {ResponseDataT[]} data - The source data.
 * @param {OrderedColumnsT} orderedColumns - The columns description.
 * @param {ResponseDataValueT[]} xAxisValues - The list of X axis values.
 * @returns {ResponseDataT[]} The filled data source.
 */
const getSourceAndFillEmptyData = (
    serieKey: ResponseDataValueT,
    data: ResponseDataT[],
    orderedColumns: OrderedColumnsT,
    xAxisValues: ResponseDataValueT[],
): ResponseDataT[] => {
    const zAxisKey = orderedColumns.z?.key;
    const xAxisKey = orderedColumns.x?.key;
    const yAxisKey = orderedColumns.y.key;
    if (zAxisKey === undefined || xAxisKey === undefined) {
        return [];
    }
    const rawSource = getSource(serieKey, data, zAxisKey);
    return xAxisValues.map((xAxisValue) => {
        const point = rawSource.find(
            (d) => JSON.stringify(d[xAxisKey]) === JSON.stringify(xAxisValue),
        );
        if (point) {
            return point;
        }
        return {
            [zAxisKey]: serieKey,
            [xAxisKey]: xAxisValue,
            [yAxisKey]: null,
            ...(isTree(xAxisValue) && {
                [`${xAxisKey}.label`]: xAxisValue.label,
                [`${xAxisKey}.value`]: xAxisValue.value,
            }),
            ...(isTree(serieKey) && {
                [`${zAxisKey}.label`]: serieKey.label,
                [`${zAxisKey}.value`]: serieKey.value,
            }),
        };
    });
};

/** Generate a dataset for a serie.
 * @param {ResponseDataValueT} serieKey - The serie key to filter on.
 * @param {ResponseDataT[]} data - The source data.
 * @param {OrderedColumnsT} orderedColumns - The columns description.
 * @param {DatasetDimension[]} dimensions - The dataset dimensions.
 * @param {boolean} [isYearlyComparison] - If the dataset should use for yearly comparison.
 * @param {ResponseDataValueT[]} [xAxisValues] - The list of X axis values.
 * @returns {Dataset | undefined} The generated dataset.
 */
const makeSerieDataset = (
    serieKey: ResponseDataValueT,
    data: ResponseDataT[],
    orderedColumns: OrderedColumnsT,
    dimensions: DatasetDimension[],
    isYearlyComparison: boolean,
    xAxisValues: ResponseDataValueT[],
): Dataset | undefined => {
    const xAxisKey = orderedColumns.x?.key;
    const zAxisKey = orderedColumns.z?.key;
    if (zAxisKey === undefined) {
        return undefined;
    }

    const source = getSourceAndFillEmptyData(
        serieKey,
        data,
        orderedColumns,
        xAxisValues,
    );
    standardSort(source, orderedColumns, xAxisValues);
    if (isYearlyComparison && xAxisKey) {
        sortForYearlyComparison(xAxisKey, source);
    }
    return [
        {
            id: `${serieKey}`,
            source,
            dimensions,
        },
    ];
};

/**
 * Generate eChart dataset ( see: https://echarts.apache.org/handbook/en/concepts/dataset )
 * from the data and orderedColumns.
 *
 * @param {ResponseDataT[]} data - The source data (usually outputed from `get_data`).
 * @param {OrderedColumnsT} orderedColumns - The X/Y/Z columns metadata
 * @param {Set<ResponseDataValueT>} seriesKeys - For the Z axis (the list of series), the set of series name.
 * @param {boolean} [isYearlyComparison] - If the dataset should be used for yearly comparison.
 * @param {number | null | undefined} [interval] - The length of the categories interval.
 * @param {ChartSettingsT['sortDimension']} [sortDimension] - The metric to use to sort the data.
 * @returns {Dataset[]} The list of generated dataset. If there is no Z axis, there will be only one dataset with the id `DEFAULT_DATASET_NAME`,
 * if there is a Z axis, there will be one dataset for each serie in `seriesKeys` with the same id/key.
 */
export const makeDataset = (
    data: ResponseDataT[],
    orderedColumns: OrderedColumnsT,
    seriesKeys: Set<ResponseDataValueT>,
    isYearlyComparison: boolean = false,
    interval?: number | null | undefined,
    sortDimension?: ChartSettingsT['sortDimension'],
): Dataset[] => {
    const columns = filter(
        orderedColumns,
        (col) => col !== undefined && col.key !== undefined,
    );
    let mData = [...data];
    const { x: xColumn, y: yColumn } = orderedColumns;
    if (interval && xColumn) {
        // We need to sort using current xAxis values when an interval is provided to maintain the order.
        const currentXAxisValues = Array.from(
            new Set(data.map((row) => row[xColumn.key])),
        );
        standardSort(mData, orderedColumns, currentXAxisValues);
    } else {
        standardSort(mData, orderedColumns);
    }

    const datasetDimensions: DatasetDimension[] = map(columns, (col) => ({
        name: col?.key ? col.key : '',
        type: convertMantyTypeToDatasetType(col?.type ? col.type : 'category'),
    }));
    const treeColumns: string[] = [];

    /* Flatten the data if needed */
    columns.forEach((column) => {
        const key = column?.key;
        if (key !== undefined) {
            mData = map(mData, (d) => flattenPoint(key, d));
            if (some(mData, (d) => isTree(d[key]))) {
                datasetDimensions.push({
                    name: `${key}.value`,
                    type: 'ordinal',
                });
                datasetDimensions.push({
                    name: `${key}.label`,
                    type: 'ordinal',
                });
                treeColumns.push(key);
            }
        }
    });
    if (orderedColumns.z?.key === undefined) {
        if (sortDimension) {
            return getSortedDataset(
                mData,
                datasetDimensions,
                sortDimension,
                treeColumns,
                yColumn,
                xColumn,
            );
        }
        return [
            {
                id: DEFAULT_DATASET_NAME,
                source: mData,
                dimensions: datasetDimensions,
            },
        ];
    }
    const orderedXAxisValues = getSortedXValues(
        mData,
        xColumn,
        yColumn,
        sortDimension,
    );
    const dataset: Dataset[] = [];
    seriesKeys.forEach((k) => {
        const serie = makeSerieDataset(
            k,
            mData,
            orderedColumns,
            datasetDimensions,
            isYearlyComparison,
            orderedXAxisValues,
        );
        if (serie !== undefined) {
            dataset.push(...serie);
        }
    });
    return dataset;
};

/**
 * Extract the list of series from the Z axis / column from a list of data point.
 *
 * We use the Z axis/column to encode the list of serie. This column can have any
 * number of unique value where each unique value is a serie.
 * To be able to get all the series, se need to iterate through all the datapoint and
 * add the value stored in their Z column inside the final set of series keys.
 *
 * @param {ResponseDataT[]} data - The list of data point where we want to extract our list of series.
 * @param {OrderedColumnsT} orderedColumns - The column description.
 * @returns {Set<ResponseDataValueT>} A set of all unique series key.
 */
export const extractSeriesKey = (
    data: ResponseDataT[],
    orderedColumns: OrderedColumnsT,
): Set<ResponseDataValueT> => {
    const zKeys: Set<ResponseDataValueT> = new Set();
    const zColumn = orderedColumns.z;
    if (zColumn !== undefined) {
        data.forEach((d) => {
            const point = d[zColumn.key];
            if (isTree(point)) {
                zKeys.add(point.value);
            } else {
                zKeys.add(point);
            }
        });
    }
    return zKeys;
};

type DatasetSerieEncode = {
    x: string | number | undefined;
    y: string | number | undefined;
};

/**
 * Return the `enode` parameter for a serie ( see: https://echarts.apache.org/handbook/en/concepts/dataset#map-from-data-to-charts-(series.encode) )
 *
 * @param {Dataset[]} datasets - The list of datasets we are using for our series.
 * @param {OrderedColumnsT} orderedColumns - The columns description.
 * @param {boolean} [isHorizontal] - If the chart is horizontal.
 * @returns {DatasetSerieEncode} The encode to use for the series.
 */
export const datasetEncode = (
    datasets: Dataset[],
    orderedColumns: OrderedColumnsT,
    isHorizontal: boolean = false,
): DatasetSerieEncode => {
    const normalEncode = { x: orderedColumns.x?.key, y: orderedColumns.y.key };

    // Special case for tree data:
    const xKey = orderedColumns.x?.key;
    if (
        xKey !== undefined &&
        some(datasets, (dt) => some(dt.source, (p) => isTree(p[xKey])))
    ) {
        normalEncode.x = `${orderedColumns.x?.key}.value`;
    }
    if (
        some(datasets, (dt) =>
            some(dt.source, (p) => isTree(p[orderedColumns.y.key])),
        )
    ) {
        normalEncode.y = `${orderedColumns.y.key}.value`;
    }

    // Swap axis if horizontal.
    return isHorizontal
        ? { x: normalEncode.y, y: normalEncode.x }
        : normalEncode;
};

/* Return the oposite dimension of the input. */
const getInverseDimension = (dimension: AxisDimensionT): AxisDimensionT => {
    switch (dimension) {
        case 'x':
            return 'y';
        case 'y':
            return 'x';
        case 'radius':
            return 'angle';
        case 'angle':
            return 'radius';
        default:
            console.error('Unknow dimension type:', dimension);
            return 'x';
    }
};

/**
 * Return the key of the value in the data point `value` object.
 * Useful when we need to get back to the value in a function like tooltip formatter.
 * @param d - The serie's data point (return by function like the tooltip formatter).
 */
export const getDatasetValueKey = (
    d: SeriePointT,
    isHorizontal = false,
): string => {
    // IMPORTANT: Anoyingly, we still need the isHorizontal + the axisDim check
    // because echart doesn't always send axisDim (for exemple stacked bar).
    // So, when it doesn't send it, we have no way to automaticaly gt the
    // dimension that has the data. So we need to revert back to an heuristic
    // to guess it.
    if ('axisDim' in d && d.axisDim !== undefined) {
        const dataAxis = getInverseDimension(d.axisDim);
        const encodedDimensionIdx = d.encode[dataAxis][0];
        return d.dimensionNames[encodedDimensionIdx];
    }

    // When we don't have the axisDim, we have to guess it.
    // The first part if finding out if we use polar or cartesian coordinate.
    let dimensionIdx = 0;
    const isPolar = 'radius' in d.encode;
    if (isPolar) {
        // In polar chart, the angle dimension always has the data.
        dimensionIdx = d.encode.angle[0];
    } else {
        // For cartesian chart, it is the y axis, inverted if we are horizontal.
        dimensionIdx = isHorizontal ? d.encode.x[0] : d.encode.y[0];
    }
    return d.dimensionNames[dimensionIdx];
};

/**
 * Extracts the values from a column (either merged or non-merged) and return a point object.
 *
 * @param {SeriePointT} d - The data point containing values.
 * @param {ResponseColumnT} column - The column (either x or z) from which values need to be extracted.
 */
const extractColumnValues = (
    d: SeriePointT,
    column: ResponseColumnT,
): ResponseDataT => {
    const extractedValues: ResponseDataT = {};

    if (column.mergedColumns) {
        column.mergedColumns.forEach((key: string) => {
            extractedValues[key] = d.value[key];
        });
    } else if (column.key) {
        extractedValues[column.key] = d.value[column.key];
    }

    return extractedValues;
};

/**
 * From a SeriePointT, usually used in tooltip formatters, extract the actual
 * value in the data set.
 *
 * This function handles both merged and non-merged columns. If the `x` or `z`
 * columns are merged (i.e., multiple individual columns are combined into a
 * single key), it will extract and return the values of each individual column
 * in the merged set.
 *
 * @param {SeriePointT} d - The serie's point description.
 * @param {OrderedColumnsT} orderedColumns - The data column description.
 *
 * @returns {ResponseDataT | undefined} The datapoint in the dataset corresponding to this serie's point.
 * return undefined if the x axis is undefined.
 */
export function getDatasetPoint(
    d: SeriePointT,
    orderedColumns: OrderedColumnsT,
): ResponseDataT | undefined {
    const { x, z } = orderedColumns;
    if (!x) return undefined;
    const point = extractColumnValues(d, x);

    if (z) {
        return { ...point, ...extractColumnValues(d, z) };
    }

    return point;
}
